import { useState } from 'react';
import { Download, X, FileText, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadMagnetModal = ({ isOpen, onClose, magnetType }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const leadMagnets = {
    'social-media': {
      title: 'Social Media Marketing Checklist 2026',
      description: 'Complete 50-point checklist to dominate social media platforms and grow your brand exponentially.',
      downloadUrl: '/assets/social-media-checklist.pdf',
    },
    'google-ads': {
      title: 'Google Ads Optimization Guide',
      description: 'Step-by-step guide to reduce cost-per-click by 40% and increase ROI on your Google Ads campaigns.',
      downloadUrl: '/assets/google-ads-guide.pdf',
    },
    'lead-generation': {
      title: 'Lead Generation Playbook for Real Estate',
      description: 'Proven strategies to generate 500+ qualified leads monthly for real estate businesses in NCR.',
      downloadUrl: '/assets/lead-gen-playbook.pdf',
    },
    'content-calendar': {
      title: '90-Day Content Calendar Template',
      description: 'Pre-planned content ideas and posting schedule to keep your social media active and engaging.',
      downloadUrl: '/assets/content-calendar.xlsx',
    },
  };

  const magnet = leadMagnets[magnetType] || leadMagnets['social-media'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/lead-magnet`, {
        ...formData,
        magnet_type: magnetType,
        magnet_title: magnet.title,
      });
      setDownloaded(true);
      toast.success('Success! Check your email for the download link.');
      
      // Simulate download
      setTimeout(() => {
        toast.info('Download started! Check your downloads folder.');
      }, 1000);
    } catch (error) {
      toast.error('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="lead-magnet-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              {downloaded ? <CheckCircle className="w-8 h-8" /> : <FileText className="w-8 h-8" />}
            </div>
            <div>
              <h3 className="text-xl font-bold">Free Download</h3>
              <p className="text-teal-100 text-sm">Instant access</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!downloaded ? (
            <>
              <h4 className="text-xl font-bold text-slate-900 mb-2">{magnet.title}</h4>
              <p className="text-slate-600 mb-6">{magnet.description}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="magnet-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="magnet-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="magnet-phone-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white px-6 py-4 rounded-xl font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="magnet-download-btn"
                >
                  <Download className="w-5 h-5" />
                  {loading ? 'Processing...' : 'Get Free Download'}
                </button>
              </form>
              <p className="text-xs text-slate-500 mt-4 text-center">
                By downloading, you agree to receive marketing emails from Ashvya Digital Media.
              </p>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">Success!</h4>
              <p className="text-slate-600 mb-6">
                We've sent the download link to your email. Check your inbox!
              </p>
              <button
                onClick={onClose}
                className="bg-slate-100 hover:bg-slate-200 text-slate-900 px-6 py-3 rounded-xl font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadMagnetModal;